<template>
    <b-container>
        <b-row>
            <b-col>
                <b-card class="mb-3">
                    <template #header>
                        <h5 class="mb-0">📊 Message Delivery Statistics</h5>
                    </template>
                    <b-overlay :show="loading" rounded="sm">
                        <div v-if="!loading && statistics">
                            <!-- Summary Cards -->
                            <b-row>
                                <b-col md="3" sm="6" class="mb-3">
                                    <b-card bg-variant="light" class="text-center">
                                        <h2 class="mb-0">{{statistics.total}}</h2>
                                        <small class="text-muted">Total Sent</small>
                                    </b-card>
                                </b-col>
                                <b-col md="3" sm="6" class="mb-3">
                                    <b-card bg-variant="success" text-variant="white" class="text-center">
                                        <h2 class="mb-0">{{getStatusCount('delivered')}}</h2>
                                        <small>✅ Delivered</small>
                                        <div class="mt-1"><small>{{getPercentage('delivered')}}%</small></div>
                                    </b-card>
                                </b-col>
                                <b-col md="3" sm="6" class="mb-3">
                                    <b-card bg-variant="danger" text-variant="white" class="text-center">
                                        <h2 class="mb-0">{{getStatusCount('failed')}}</h2>
                                        <small>❌ Failed</small>
                                        <div class="mt-1"><small>{{getPercentage('failed')}}%</small></div>
                                    </b-card>
                                </b-col>
                                <b-col md="3" sm="6" class="mb-3">
                                    <b-card bg-variant="warning" text-variant="white" class="text-center">
                                        <h2 class="mb-0">{{getStatusCount('undelivered')}}</h2>
                                        <small>⚠️ Undelivered</small>
                                        <div class="mt-1"><small>{{getPercentage('undelivered')}}%</small></div>
                                    </b-card>
                                </b-col>
                            </b-row>

                            <!-- Additional Status Row -->
                            <b-row>
                                <b-col md="4" sm="6" class="mb-3">
                                    <b-card bg-variant="info" text-variant="white" class="text-center">
                                        <h2 class="mb-0">{{getStatusCount('read')}}</h2>
                                        <small>📖 Read</small>
                                        <div class="mt-1"><small>{{getPercentage('read')}}%</small></div>
                                    </b-card>
                                </b-col>
                                <b-col md="4" sm="6" class="mb-3">
                                    <b-card bg-variant="secondary" text-variant="white" class="text-center">
                                        <h2 class="mb-0">{{getStatusCount('sent')}}</h2>
                                        <small>📤 Sent</small>
                                        <div class="mt-1"><small>{{getPercentage('sent')}}%</small></div>
                                    </b-card>
                                </b-col>
                                <b-col md="4" sm="6" class="mb-3">
                                    <b-card class="text-center">
                                        <h2 class="mb-0">{{getPendingCount()}}</h2>
                                        <small class="text-muted">⏳ Pending/Other</small>
                                        <div class="mt-1"><small>{{getPendingPercentage()}}%</small></div>
                                    </b-card>
                                </b-col>
                            </b-row>

                            <!-- Error Details Section -->
                            <b-row v-if="hasErrors" class="mt-4">
                                <b-col>
                                    <h5 class="mb-3">🔍 Error Details ({{getTotalErrors()}} total failures)</h5>
                                    <b-alert
                                        v-for="(count, errorCode) in statistics.error_details"
                                        :key="errorCode"
                                        show
                                        variant="danger"
                                        class="mb-2">
                                        <div class="d-flex justify-content-between align-items-start">
                                            <div>
                                                <strong>Error {{errorCode}} - {{getErrorTitle(errorCode)}}</strong>
                                                <span class="badge badge-dark ml-2">{{count}} {{count === 1 ? 'failure' : 'failures'}}</span>
                                                <p class="mb-1 mt-2">{{getErrorDescription(errorCode)}}</p>
                                                <a :href="getErrorDocsUrl(errorCode)" target="_blank" rel="noopener">
                                                    View Twilio Documentation →
                                                </a>
                                            </div>
                                        </div>
                                    </b-alert>
                                </b-col>
                            </b-row>

                            <!-- No Errors Message -->
                            <b-row v-else-if="!hasErrors && statistics.total > 0" class="mt-4">
                                <b-col>
                                    <b-alert show variant="success">
                                        🎉 No delivery errors found! All messages have been processed successfully.
                                    </b-alert>
                                </b-col>
                            </b-row>
                        </div>

                        <!-- No Data Message -->
                        <div v-if="!loading && (!statistics || statistics.total === 0)">
                            <b-alert show variant="info">
                                No statistics available yet. Statistics will appear once messages are sent and delivery statuses are received.
                            </b-alert>
                        </div>
                    </b-overlay>
                </b-card>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import { MessageApi } from "@/app/messages/messageApi";
import handleError from "@/errorHandler";

// Common SMS Error Codes Mapping
const COMMON_SMS_ERROR_CODES = {
  '21211': {
    title: "Invalid 'To' Phone Number",
    description: "The phone number is not in a valid format.",
    docs_url: "https://www.twilio.com/docs/api/errors/21211"
  },
  '21614': {
    title: "Not a Mobile Number",
    description: "The number is a landline or cannot receive SMS.",
    docs_url: "https://www.twilio.com/docs/api/errors/21614"
  },
  '21610': {
    title: "Unsubscribed Recipient",
    description: "The recipient previously replied STOP to unsubscribe.",
    docs_url: "https://www.twilio.com/docs/api/errors/21610"
  },
  '30007': {
    title: "Message Filtered",
    description: "The carrier rejected this message.",
    docs_url: "https://www.twilio.com/docs/api/errors/30007"
  },
  '30008': {
    title: "Unknown Destination",
    description: "Cannot route to this phone number.",
    docs_url: "https://www.twilio.com/docs/api/errors/30008"
  },
  '30006': {
    title: "Landline or Unreachable",
    description: "The number is unreachable or a landline.",
    docs_url: "https://www.twilio.com/docs/api/errors/30006"
  },
  '30005': {
    title: "Unknown Destination Handset",
    description: "Unable to deliver to this handset.",
    docs_url: "https://www.twilio.com/docs/api/errors/30005"
  },
  '30004': {
    title: "Message Blocked",
    description: "Message delivery is blocked by carrier.",
    docs_url: "https://www.twilio.com/docs/api/errors/30004"
  },
  '21408': {
    title: "Permission to Send SMS Denied",
    description: "Your account doesn't have permission to send to this number.",
    docs_url: "https://www.twilio.com/docs/api/errors/21408"
  },
  '21612': {
    title: "'From' Number Not Supported",
    description: "The From number is not SMS-capable.",
    docs_url: "https://www.twilio.com/docs/api/errors/21612"
  }
};

export default {
    name: "WardMessageStatistics",
    props: {
        wardId: {
            type: String,
            required: true
        },
        messageId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            statistics: null,
            loading: false
        };
    },
    computed: {
        hasErrors() {
            return this.statistics && this.statistics.error_details && Object.keys(this.statistics.error_details).length > 0;
        }
    },
    async mounted() {
        await this.fetchStatistics();
    },
    methods: {
        async fetchStatistics() {
            this.loading = true;
            try {
                const { data } = await MessageApi.getMessageStatistics(this.wardId, this.messageId);
                this.statistics = data;
            } catch (e) {
                handleError(e, "Failed to fetch message statistics");
            } finally {
                this.loading = false;
            }
        },
        getStatusCount(status) {
            if (!this.statistics || !this.statistics.status_counts) return 0;
            return this.statistics.status_counts[status] || 0;
        },
        getPercentage(status) {
            if (!this.statistics || this.statistics.total === 0) return 0;
            const count = this.getStatusCount(status);
            return ((count / this.statistics.total) * 100).toFixed(1);
        },
        getPendingCount() {
            if (!this.statistics) return 0;
            const knownStatuses = ['delivered', 'failed', 'undelivered', 'read', 'sent'];
            let knownCount = 0;
            Object.keys(this.statistics.status_counts || {}).forEach(status => {
                if (knownStatuses.includes(status)) {
                    knownCount += this.statistics.status_counts[status];
                }
            });
            return this.statistics.total - knownCount;
        },
        getPendingPercentage() {
            if (!this.statistics || this.statistics.total === 0) return 0;
            return ((this.getPendingCount() / this.statistics.total) * 100).toFixed(1);
        },
        getTotalErrors() {
            if (!this.hasErrors) return 0;
            return Object.values(this.statistics.error_details).reduce((sum, count) => sum + count, 0);
        },
        getErrorTitle(errorCode) {
            const errorInfo = COMMON_SMS_ERROR_CODES[errorCode];
            return errorInfo ? errorInfo.title : `Error ${errorCode}`;
        },
        getErrorDescription(errorCode) {
            const errorInfo = COMMON_SMS_ERROR_CODES[errorCode];
            if (errorInfo) {
                return errorInfo.description;
            }
            return "An error occurred during delivery. Click the link below for more details.";
        },
        getErrorDocsUrl(errorCode) {
            const errorInfo = COMMON_SMS_ERROR_CODES[errorCode];
            if (errorInfo) {
                return errorInfo.docs_url;
            }
            if (errorCode === 'unknown' || !errorCode) {
                return "https://www.twilio.com/docs/api/errors";
            }
            return `https://www.twilio.com/docs/api/errors/${errorCode}`;
        }
    }
};
</script>

<style scoped>
.badge {
    font-size: 0.875rem;
}
</style>

